/**
 * Strava upload flow (SPEC §11.2/11.3): POST the FIT via the Worker, poll until there is an activity id or an
 * error. Policy (default "ask"): Mario also records on his Edge → Garmin → Strava would create duplicates.
 */
import { db } from '@/db/db';
import type { RideRecord, StravaStatus } from '@/ride/model';
import { api } from './api';

export const STRAVA_ACTIVITY_URL = (id: number) => `https://www.strava.com/activities/${id}`;

async function setStatus(rideId: string, strava: StravaStatus): Promise<void> {
  await db.rides.update(rideId, { strava, updatedAt: Date.now() });
}

export function rideDescription(r: RideRecord): string {
  const s = r.summary;
  const parts = [
    `VeloStube indoor ride${r.routeName ? ` – ${r.routeName}` : ''}${r.workoutName ? ` – ${r.workoutName}` : ''}`,
    `NP ${Math.round(s.np)} W · IF ${s.if.toFixed(2)} · TSS ${Math.round(s.tss)}`,
  ];
  return parts.join('\n');
}

export async function uploadRideToStrava(
  ride: RideRecord,
  opts: { pollMs?: number; maxPolls?: number; onStatus?: (s: StravaStatus) => void } = {},
): Promise<StravaStatus> {
  const data = await db.rideData.get(ride.id);
  if (!data?.fit) {
    const st: StravaStatus = { state: 'error', message: 'Keine FIT-Datei vorhanden' };
    await setStatus(ride.id, st);
    return st;
  }
  const report = async (st: StravaStatus) => {
    await setStatus(ride.id, st);
    opts.onStatus?.(st);
    return st;
  };
  try {
    const up = await api.stravaUpload(ride.id, data.fit, ride.name, rideDescription(ride));
    await report({ state: 'uploading', uploadId: up.id });
    if (up.activity_id) return report({ state: 'done', activityId: up.activity_id });
    for (let i = 0; i < (opts.maxPolls ?? 30); i++) {
      await new Promise((r) => setTimeout(r, opts.pollMs ?? 2000));
      const st = await api.stravaUploadStatus(up.id, ride.id);
      if (st.error) return report({ state: 'error', message: st.error });
      if (st.activity_id) return report({ state: 'done', activityId: st.activity_id });
    }
    return report({ state: 'error', message: 'Zeitüberschreitung beim Warten auf Strava' });
  } catch (err) {
    return report({ state: 'error', message: err instanceof Error ? err.message : String(err) });
  }
}
